import { useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Approximate menu heights for viewport flip before the menu is measured (py-2 + items with py-1.5). */
export const ROW_ACTIONS_MENU_HEIGHT = {
  SINGLE: 52,
  DOUBLE: 92,
  TRIPLE: 132,
  QUADRUPLE: 172,
  QUINT: 212,
  SEXTUPLE: 252,
  SEPTUPLE: 292,
};

/**
 * Fixed-position layout for admin table row action menus so dropdowns
 * do not expand overflow containers (e.g. last row in overflow-x-auto tables).
 * Measures rendered menu height when available for accurate flip/clamping.
 */
export default function useFixedRowActionsMenu(
  rowActionsOpen,
  rowActionsRefs,
  tableScrollRef,
  menuHeight = ROW_ACTIONS_MENU_HEIGHT.SINGLE
) {
  const menuRef = useRef(null);
  const openRowId = useMemo(
    () => Object.keys(rowActionsOpen).find((k) => rowActionsOpen[k]),
    [rowActionsOpen]
  );
  const [layout, setLayout] = useState(null);
  const [placementReady, setPlacementReady] = useState(false);

  useLayoutEffect(() => {
    if (!openRowId) {
      setLayout(null);
      setPlacementReady(false);
      return;
    }
    const el = rowActionsRefs.current[openRowId];
    if (!el) {
      setLayout(null);
      setPlacementReady(false);
      return;
    }

    setPlacementReady(false);

    const updateLayout = () => {
      const r = el.getBoundingClientRect();
      const gap = 4;
      const viewportPad = 8;
      const measured = menuRef.current?.offsetHeight;
      const h = measured && measured > 0 ? measured : menuHeight;
      const spaceBelow = window.innerHeight - r.bottom;
      let top = r.bottom + gap;
      if (spaceBelow < h + gap) {
        top = Math.max(viewportPad, r.top - h - gap);
      }
      top = Math.min(Math.max(viewportPad, top), window.innerHeight - h - viewportPad);
      setLayout({
        top,
        right: window.innerWidth - r.right,
      });
    };

    updateLayout();

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        updateLayout();
        setPlacementReady(true);
      });
    });

    const tableEl = tableScrollRef?.current;
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    tableEl?.addEventListener('scroll', updateLayout, { passive: true });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
      tableEl?.removeEventListener('scroll', updateLayout);
    };
  }, [openRowId, menuHeight, rowActionsRefs, tableScrollRef]);

  return { layout, menuRef, placementReady };
}
